import EntityModel from '@/common/models/entity-model';
import TagModel from '@/common/models/tag-model';
import ContactModel from '@/common/models/contact-model';
import LeadSourceModel from '@/common/models/lead-source-model';
import PaymentInstrumentModel from '@/common/models/payment-instrument-model';
import PaymentInstrumentExpandedModel from '@/common/models/payment-instrument-expanded-model';
import emptyInnerHTML from '@/common/helpers/empty-inner-html';

export default class CustomerModel extends EntityModel {
    hydrate({
        createdTime = null,
        customFields = null,
        email = null,
        id = null,
        ipAddress = null,
        invoiceCount = null,
        primaryAddress = null,
        updatedTime = null,
        lifetimeRevenue = null, // {amount, currency, usdAmount}
        averageValue = null, // {amount, currency, usdAmount}
        lastPaymentTime = null,
        paymentCount = null,
        tags = [],
        websiteId = null,
        ...otherFields
    } = {}) {
        this.createdTime = createdTime;
        this.customFields = customFields;
        this.email = email;
        this.id = id;
        this.ipAddress = ipAddress;
        this.invoiceCount = invoiceCount;
        this.primaryAddress = new ContactModel(primaryAddress || {});
        this.updatedTime = updatedTime;
        this.lifetimeRevenue = lifetimeRevenue;
        this.averageValue = averageValue;
        this.lastPaymentTime = lastPaymentTime;
        this.paymentCount = paymentCount;
        this.tags = tags.map(tag => new TagModel(tag));
        this.websiteId = websiteId;

        // embedded entities
        this.leadSource = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'leadSource',
            model: LeadSourceModel,
        });

        if (EntityModel.hasEmbedded({
            fields: otherFields,
            entityName: 'defaultPaymentInstrument',
        })) {
            this.defaultPaymentInstrument = EntityModel.fromEmbedded({
                fields: otherFields,
                entityName: 'defaultPaymentInstrument',
                model: PaymentInstrumentExpandedModel,
            });
        } else {
            this.defaultPaymentInstrument = new PaymentInstrumentModel(
                otherFields.defaultPaymentInstrument || undefined,
            );
        }
    }

    get hasCustomFields() {
        return Object.keys(this.customFields || {}).length > 0;
    }

    get displayName() {
        const {
            fullName,
            organization,
            primaryEmailAddress,
        } = this.primaryAddress;

        // Unlike contact, prioritize the organization name for customer.
        return `${organization || ''}`.trim()
            || fullName
            || primaryEmailAddress
            || this.id;
    }

    get initials() {
        const getInitial = name => (name ? name.charAt(0) : '');
        return (`${getInitial(this.primaryAddress.firstName)}${getInitial(this.primaryAddress.lastName)}`.trim()) || emptyInnerHTML;
    }

    getPayload() {
        const payload = {
            data: {
                websiteId: this.websiteId,
                paymentToken: this.paymentToken,
                customFields: this.customFields,
                primaryAddress: this.primaryAddress.getPayload(),
            },
        };
        if (this.id !== null) {
            payload.id = this.id;
        }
        return payload;
    }
}
